import React, { useState } from "react";
import axios, { API } from "../api";

/**
 * Inline crisis-card rendered beside Aura's message when the backend returns
 * a `crisis_resources` payload.
 *
 * Two-step UX (per Feb 2026 user feedback):
 *   Step 1 — Aura asks: "Want me to make the call for you?" with three soft buttons:
 *            [Yes, call for me]  [Just show me the lines]  [I'm okay]
 *   Step 2 — If "Yes": tries `/api/call/dispatch` for Twilio-powered call; if it
 *            fails, degrades to a plain tel: link. Resource list still rendered.
 *          — If "Just show me": skips the call and renders the resource list.
 *          — If "I'm okay": collapses politely.
 *
 * Resources render as tap-targets, each with an icon (📞/💬/🌐) so users can
 * see at a glance which channel they're picking.
 */
export function CrisisCard({ payload }) {
  const {
    headline = "I'm right here.",
    subline = "These lines are free, confidential, and trained for exactly this.",
    resources = [],
    pre_call_offer: preCallOffer = false,
    pre_call_text: preCallText = "Want me to call for you, or should I just show you the lines?",
  } = payload || {};

  // step: 'ask' (Aura offering to call) | 'resources' (show list) | 'dismissed'
  const [step, setStep] = useState(preCallOffer ? "ask" : "resources");
  const [callStatus, setCallStatus] = useState(null); // null | 'dialing' | 'connected' | 'fallback' | 'error'
  const [callMessage, setCallMessage] = useState("");

  if (step === "dismissed") {
    return (
      <div className="crisis-card crisis-card-dismissed" data-testid="crisis-card-dismissed">
        <span>I'm staying right here. Whenever you're ready, just say the word. 💜</span>
      </div>
    );
  }

  // Phase 25 — medical emergencies get a louder visual treatment so it's
  // unmistakable. Same component, just an extra class.
  const isMedical = payload?.category === "medical_emergency";

  const primaryResource = resources[0];

  const handleAuraCall = async () => {
    if (!primaryResource?.href) {
      setStep("resources");
      return;
    }
    setCallStatus("dialing");
    setCallMessage("Connecting you now…");
    try {
      const r = await axios.post(`${API}/call/dispatch`, {
        hotline_href: primaryResource.href,
        category: payload?.category || null,
      });
      if (r.data?.ok) {
        setCallStatus("connected");
        setCallMessage("Calling your phone now — pick up when it rings. 💜");
      } else {
        // Twilio not configured / no phone on file → degrade to tel: link
        setCallStatus("fallback");
        const reason = r.data?.reason;
        if (reason === "no_phone_on_file") {
          setCallMessage("Add your phone in Settings → Crisis Call so I can dial you next time. For now, tap a line below to call directly.");
        } else {
          setCallMessage("Tap a line below to call directly — I'm right here.");
        }
        setStep("resources");
      }
    } catch (e) {
      setCallStatus("error");
      setCallMessage("Couldn't dial through me — tap a line below to call directly.");
      setStep("resources");
    }
  };

  const handleShowResources = () => setStep("resources");
  const handleDismiss = () => setStep("dismissed");

  return (
    <div className={`crisis-card${isMedical ? ' crisis-card-medical' : ''}`} data-testid="crisis-card">
      <div className="crisis-card-header">
        <span className="crisis-card-icon">{isMedical ? '🚨' : '💜'}</span>
        <div className="crisis-card-text">
          <strong>{headline}</strong>
          <span>{subline}</span>
        </div>
      </div>

      {step === "ask" && (
        <div className="crisis-card-ask" data-testid="crisis-card-ask">
          <p className="crisis-card-ask-text">{preCallText}</p>
          <div className="crisis-card-ask-buttons">
            <button
              className="crisis-btn crisis-btn-primary"
              onClick={handleAuraCall}
              data-testid="crisis-card-call-yes"
            >
              📞 Yes, call for me
            </button>
            <button
              className="crisis-btn crisis-btn-secondary"
              onClick={handleShowResources}
              data-testid="crisis-card-show-lines"
            >
              Just show me the lines
            </button>
            <button
              className="crisis-btn crisis-btn-ghost"
              onClick={handleDismiss}
              data-testid="crisis-card-im-okay"
            >
              I'm okay
            </button>
          </div>
        </div>
      )}

      {callMessage && step !== "ask" && (
        <div className={`crisis-card-call-status crisis-card-call-${callStatus || "info"}`}>
          {callMessage}
        </div>
      )}

      {step === "resources" && resources.length > 0 && (
        <div className="crisis-card-resources" data-testid="crisis-card-resources">
          {resources.map((r, i) => (
            <a
              key={i}
              href={r.href}
              target={r.kind === "web" ? "_blank" : undefined}
              rel={r.kind === "web" ? "noopener noreferrer" : undefined}
              className="crisis-card-resource"
              data-testid={`crisis-card-resource-${i}`}
            >
              <span className="crisis-card-resource-icon">
                {r.kind === "voice" ? "📞" : r.kind === "sms" ? "💬" : "🌐"}
              </span>
              <span className="crisis-card-resource-body">
                <strong>{r.label}</strong>
                <span>{r.description}</span>
              </span>
            </a>
          ))}
          {callStatus !== "connected" && (
            <button
              className="crisis-card-dismiss-link"
              onClick={handleDismiss}
              data-testid="crisis-card-dismiss"
            >
              I'm okay for now
            </button>
          )}
        </div>
      )}
    </div>
  );
}

export default CrisisCard;
